// Fetch the login modal's chunk, then hand off to ./openLoginModalWhenReady.
//
// NOTHING WITH A BRANCH IN IT GOES IN THIS FILE. jest 27 cannot execute the
// `import()` below (esbuild-jest leaves it native), so anything here is untested —
// which is how a mount-ordering bug shipped once. Logic belongs in the sibling.
//
// The import is dynamic because registering the modal through registerForRails is
// a static import and would put ~135KB of source on the critical path of the
// largest organic landing page, for every visitor including control.
import type { LoginModalEventDetail } from 'js/login_modal/LoginModalManager';

import { memoizeAsync } from './memoizeAsync';
import { openLoginModalWhenReady } from './openLoginModalWhenReady';

// memoizeAsync, not `??=`: a rejected chunk must not be cached, or one network blip
// pins the pageview to the fallback redirect. The branch that does that lives in
// memoizeAsync so it is covered by tests — see this file's header rule.
const load = memoizeAsync(() =>
  Promise.all([
    import('js/login_modal/LoginModalManager'),
    import('react'),
    import('react-dom/client'),
  ]).then(([modal, React, ReactDOM]) => ({
    LoginModalManager: modal.LoginModalManager as unknown,
    openLoginModal: modal.openLoginModal as (detail: unknown) => void,
    // Passed through rather than wrapped in arrows: a wrapper returning
    // React.createElement(...) reads as a component definition to eslint-plugin-react.
    createElement: React.createElement as unknown as (type: unknown) => unknown,
    createRoot: ReactDOM.createRoot as unknown as (container: Element) => {
      render: (node: unknown) => void;
    },
  })),
);

/**
 * Open the shared login modal, fetching and mounting it on first use.
 * Rejects if the chunk cannot load or the modal never opens, so the caller can
 * fall back to the server-side signup redirect.
 */
export function lazyMountLoginModal(
  detail: LoginModalEventDetail,
): Promise<void> {
  return load().then((deps) => openLoginModalWhenReady(deps, detail));
}

export default lazyMountLoginModal;
